import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Plus, Trash2, Upload } from "lucide-react";
import * as api from "../api/api";
import "./AdminEdit.css";
import LoadingSpinner from "./LoadingSpinner";

const AdminGalleryEdit = () => {
  const navigate = useNavigate();
  const { locationSlug } = useParams();

  const [gallery, setGallery] = useState([]);
  const [pendingGallery, setPendingGallery] = useState({ add: [], remove: [] }); // <- modifications non enregistrées
  const [saved, setSaved] = useState(false);
  const [newImageAlt, setNewImageAlt] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fileInputRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      try {
        const res = await api.getGallery(locationSlug);
        if (mounted && typeof res !== "undefined") setGallery(res);
      } catch (err) {
        console.error("Failed to load gallery for", locationSlug, err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => {
      mounted = false;
    };
  }, [locationSlug]);

  // -----------------------------------------------------
  // Gestion locale : ajout "virtuel" à la galerie
  // -----------------------------------------------------
  const handleAddImage = () => {
    if (!uploadFile) {
      alert("Sélectionnez un fichier avant d'ajouter");
      return;
    }
    const tempId = `temp-${Date.now()}`;
    const tempURL = URL.createObjectURL(uploadFile);

    const newImage = {
      id: tempId,
      url: tempURL,
      alt: newImageAlt || "Nouvelle image",
      _file: uploadFile, // on garde le fichier réel pour upload ultérieur
    };

    setGallery((prev) => [...prev, newImage]);
    setPendingGallery((prev) => ({ ...prev, add: [...prev.add, newImage] }));

    setUploadFile(null);
    setNewImageAlt("");
    fileInputRef.current.value = null;
  };

  // -----------------------------------------------------
  // Gestion locale : suppression "virtuelle"
  // -----------------------------------------------------
  const handleRemoveImage = (image) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette image ?"))
      return;

    setGallery((prev) => prev.filter((img) => img.id !== image.id));

    // Si c'était une image déjà existante (pas ajoutée récemment)
    if (!image.id.startsWith("temp-")) {
      setPendingGallery((prev) => ({
        ...prev,
        remove: [...prev.remove, image.id],
      }));
    } else {
      // Si elle faisait partie des ajouts récents
      setPendingGallery((prev) => ({
        ...prev,
        add: prev.add.filter((img) => img.id !== image.id),
      }));
    }
  };

  // -----------------------------------------------------
  // Validation finale : upload + suppression côté serveur
  // -----------------------------------------------------
  const handleSave = async () => {
    try {
      // Uploads
      for (const img of pendingGallery.add) {
        await api.uploadGalleryImage(locationSlug, img._file, img.alt);
      }

      // Suppressions
      for (const id of pendingGallery.remove) {
        await api.deleteGalleryImage(locationSlug, id);
      }

      // Rechargement galerie depuis le serveur
      const updated = await api.getGallery(locationSlug);
      setGallery(updated);

      // Reset des pending
      setPendingGallery({ add: [], remove: [] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error("Erreur lors de l'enregistrement", err);
      alert(
        "Une erreur est survenue lors de l'enregistrement. Voir la console.",
      );
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="admin-edit-page">
      <div className="admin-header">
        <div className="container">
          <button
            className="back-btn"
            onClick={() =>
              navigate("/admin/dashboard", {
                state: { selectedLocation: locationSlug },
              })
            }
          >
            <ArrowLeft size={20} /> Retour au dashboard
          </button>
          <div className="admin-header-content">
            <h1 className="heading-md">Modifier - Galerie</h1>
            <button
              className="btn-primary"
              onClick={handleSave}
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <Save size={18} /> {saved ? "✓ Sauvegardé" : "Sauvegarder"}
            </button>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div className="edit-section">
            <h2 className="heading-sm">Ajouter une Nouvelle Image</h2>
            <div className="add-image-form">
              <div className="form-group">
                <label className="form-label">Uploader un fichier</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="form-input"
                  style={{ display: "none" }}
                  onChange={(e) => setUploadFile(e.target.files[0] || null)}
                />
                <div className="upload-actions">
                  <button
                    className="btn-primary"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Plus size={18} /> Choisir un fichier
                  </button>
                </div>

                {uploadFile && (
                  <input
                    type="text"
                    className="form-input file-name-banner"
                    value={uploadFile.name}
                    readOnly
                  />
                )}
              </div>

              {uploadFile && (
                <div className="form-group">
                  <label className="form-label">Description (alt text)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newImageAlt}
                    onChange={(e) => setNewImageAlt(e.target.value)}
                    placeholder="Description de l'image"
                  />
                </div>
              )}

              {uploadFile && (
                <button className="btn-secondary" onClick={handleAddImage}>
                  <Upload size={16} /> Ajouter à la galerie
                </button>
              )}
            </div>
          </div>

          <div className="edit-section">
            <h2 className="heading-sm">Images Actuelles ({gallery.length})</h2>
            <div className="gallery-grid-admin">
              {gallery.map((image) => (
                <div key={image.id} className="gallery-item-admin">
                  <img src={image.url} alt={image.alt} />
                  <div className="gallery-item-overlay">
                    <p className="gallery-item-alt">{image.alt}</p>
                    <button
                      className="btn-delete"
                      onClick={() => handleRemoveImage(image)}
                    >
                      <Trash2 size={16} /> Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {gallery.length === 0 && (
              <div className="empty-state">
                <Upload size={48} />
                <p className="body-lg">Aucune image dans la galerie</p>
                <p
                  className="body-md"
                  style={{ color: "var(--text-secondary)" }}
                >
                  Ajoutez votre première image ci-dessus
                </p>
              </div>
            )}
          </div>

          <div className="bottom-actions">
            <button className="btn-primary btn-large" onClick={handleSave}>
              <Save size={20} /> Enregistrer toutes les modifications
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminGalleryEdit;
